/**
 * Tipuri TypeScript pentru modulul Devices (Aparate medicale)
 */

// Enums
export type DeviceCategory = 'imaging' | 'laboratory' | 'functional';
export type DeviceStatus = 'active' | 'maintenance' | 'inactive';
export type ExceptionType = 'maintenance' | 'holiday' | 'special' | 'closed';

// Device Type (seed data - readonly)
export interface DeviceType {
  id: number;
  code: string;
  name: string;
  category: DeviceCategory;
  modality_code: string;
  default_slot_minutes: number;
  icon: string | null;
  is_active: boolean;
}

// Device Schedule (program pe zi)
export interface DeviceSchedule {
  id: string;
  device_id: string;
  day_of_week: number; // 0=Luni, 6=Duminică
  start_time: string; // HH:MM:SS
  end_time: string;
  break_start: string | null;
  break_end: string | null;
  is_active: boolean;
}

export interface DeviceScheduleCreate {
  day_of_week: number;
  start_time: string;
  end_time: string;
  break_start?: string | null;
  break_end?: string | null;
  is_active?: boolean;
}

// Device Exception (excepții de la program)
export interface DeviceException {
  id: string;
  device_id: string;
  exception_type: ExceptionType;
  date: string; // YYYY-MM-DD
  start_time: string | null;
  end_time: string | null;
  reason: string;
  created_by_id: string;
  created_at: string;
}

export interface DeviceExceptionCreate {
  exception_type: ExceptionType;
  date: string;
  start_time?: string | null;
  end_time?: string | null;
  reason: string;
}

// Device
export interface Device {
  id: string;
  tenant_id: string;
  institution_id: string;
  device_type_id: number;
  code: string;
  name: string;
  brand: string | null;
  model: string | null;
  serial_number: string | null;
  building: string | null;
  floor: string | null;
  room: string;
  aet: string | null;
  pacs_id: string | null;
  status: DeviceStatus;
  slot_duration_minutes: number;
  max_daily_slots: number | null;
  created_at: string;
  updated_at: string;
  device_type: DeviceType;
  schedules: DeviceSchedule[];
}

export interface DeviceListItem {
  id: string;
  code: string;
  name: string;
  status: DeviceStatus;
  room: string;
  device_type_code: string;
  device_type_name: string;
  device_type_icon: string | null;
  institution_id: string;
  institution_name: string | null;
}

export interface DeviceCreate {
  institution_id: string;
  device_type_id: number;
  code: string;
  name: string;
  brand?: string | null;
  model?: string | null;
  serial_number?: string | null;
  building?: string | null;
  floor?: string | null;
  room: string;
  aet?: string | null;
  pacs_id?: string | null;
  slot_duration_minutes?: number;
  max_daily_slots?: number | null;
}

export interface DeviceUpdate {
  device_type_id?: number;
  code?: string;
  name?: string;
  brand?: string | null;
  model?: string | null;
  serial_number?: string | null;
  building?: string | null;
  floor?: string | null;
  room?: string;
  aet?: string | null;
  pacs_id?: string | null;
  slot_duration_minutes?: number;
  max_daily_slots?: number | null;
  status?: DeviceStatus;
}

export interface DeviceStatusChange {
  status: DeviceStatus;
  reason?: string;
}

// List Response
export interface DevicesListResponse {
  items: DeviceListItem[];
  total: number;
  skip: number;
  limit: number;
}

// Filters
export interface DevicesFilters {
  institutionId?: string;
  deviceTypeId?: number;
  status?: DeviceStatus;
  search?: string;
  skip?: number;
  limit?: number;
}

// Day of week labels
export const DAY_OF_WEEK_LABELS: Record<number, string> = {
  0: 'Luni',
  1: 'Marți',
  2: 'Miercuri',
  3: 'Joi',
  4: 'Vineri',
  5: 'Sâmbătă',
  6: 'Duminică',
};

// Status labels and colors
export const DEVICE_STATUS_CONFIG: Record<DeviceStatus, { label: string; color: 'success' | 'warning' | 'error' }> = {
  active: { label: 'Activ', color: 'success' },
  maintenance: { label: 'Mentenanță', color: 'warning' },
  inactive: { label: 'Inactiv', color: 'error' },
};

// Exception type labels and colors
export const EXCEPTION_TYPE_CONFIG: Record<ExceptionType, { label: string; color: 'warning' | 'info' | 'secondary' | 'error' }> = {
  maintenance: { label: 'Mentenanță', color: 'warning' },
  holiday: { label: 'Sărbătoare', color: 'info' },
  special: { label: 'Special', color: 'secondary' },
  closed: { label: 'Închis', color: 'error' },
};
